import React, { useEffect, useRef } from 'react'

interface Particle {
  x: number
  y: number
  z: number
  vx: number
  vy: number
  vz: number
  r: number
}

export function Background3D() {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    let animId: number
    const particles: Particle[] = []
    const COUNT = 90
    const DEPTH = 800
    const MAX_DIST = 160
    let W = 0
    let H = 0

    const resize = () => {
      W = canvas.width = canvas.offsetWidth
      H = canvas.height = canvas.offsetHeight
    }
    resize()
    window.addEventListener('resize', resize)

    for (let i = 0; i < COUNT; i++) {
      particles.push({
        x: (Math.random() - 0.5) * 1400,
        y: (Math.random() - 0.5) * 1000,
        z: Math.random() * DEPTH,
        vx: (Math.random() - 0.5) * 0.3,
        vy: (Math.random() - 0.5) * 0.2,
        vz: (Math.random() - 0.5) * 0.4,
        r: Math.random() * 1.5 + 0.5,
      })
    }

    const project = (x: number, y: number, z: number) => {
      const fov = 500
      const scale = fov / (fov + z)
      return {
        sx: x * scale + W / 2,
        sy: y * scale + H / 2,
        scale,
      }
    }

    const draw = () => {
      ctx.clearRect(0, 0, W, H)

      // Sort by z for depth
      particles.sort((a, b) => b.z - a.z)

      // Update
      for (const p of particles) {
        p.x += p.vx
        p.y += p.vy
        p.z += p.vz
        if (p.z > DEPTH) p.z = 0
        if (p.z < 0) p.z = DEPTH
        if (p.x > 700) p.x = -700
        if (p.x < -700) p.x = 700
        if (p.y > 500) p.y = -500
        if (p.y < -500) p.y = 500
      }

      // Draw connections
      for (let i = 0; i < particles.length; i++) {
        const a = particles[i]
        const pa = project(a.x, a.y, a.z)
        for (let j = i + 1; j < particles.length; j++) {
          const b = particles[j]
          const dx = a.x - b.x
          const dy = a.y - b.y
          const dz = a.z - b.z
          const dist = Math.sqrt(dx * dx + dy * dy + dz * dz)
          if (dist < MAX_DIST) {
            const pb = project(b.x, b.y, b.z)
            const alpha = (1 - dist / MAX_DIST) * 0.18 * pa.scale
            const grad = ctx.createLinearGradient(pa.sx, pa.sy, pb.sx, pb.sy)
            grad.addColorStop(0, `rgba(99,102,241,${alpha})`)
            grad.addColorStop(1, `rgba(139,92,246,${alpha})`)
            ctx.beginPath()
            ctx.moveTo(pa.sx, pa.sy)
            ctx.lineTo(pb.sx, pb.sy)
            ctx.strokeStyle = grad
            ctx.lineWidth = pa.scale * 0.8
            ctx.stroke()
          }
        }
      }

      // Draw particles
      for (const p of particles) {
        const { sx, sy, scale } = project(p.x, p.y, p.z)
        const radius = p.r * scale * 2
        const alpha = scale * 0.85

        // Glow
        const glow = ctx.createRadialGradient(sx, sy, 0, sx, sy, radius * 4)
        glow.addColorStop(0, `rgba(129,140,248,${alpha * 0.4})`)
        glow.addColorStop(1, `rgba(129,140,248,0)`)
        ctx.beginPath()
        ctx.arc(sx, sy, radius * 4, 0, Math.PI * 2)
        ctx.fillStyle = glow
        ctx.fill()

        // Core dot
        ctx.beginPath()
        ctx.arc(sx, sy, radius, 0, Math.PI * 2)
        ctx.fillStyle = `rgba(165,180,252,${alpha})`
        ctx.fill()
      }

      animId = requestAnimationFrame(draw)
    }

    draw()
    return () => {
      cancelAnimationFrame(animId)
      window.removeEventListener('resize', resize)
    }
  }, [])

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-none"
      style={{ opacity: 0.6 }}
    />
  )
}
